import React from 'react';
import { Label, Col, Table, Tooltip } from 'reactstrap';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { faInfoCircle, faChevronRight, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';
import { useCounterAppraisalAnalysis } from '../../hooks/usePricing/useCounterAppraisalAnalysis';
import { t } from '../common/Translate';
import { currencySign } from '../../Utils/currencySign';

function CounterAppraisalAnalysis() {
  const {
    expertiseCosts,
    gapBodywork,
    gapServicing,
    gapWheels,
    gapInterior,
    gapRoadTest,
    gapMotor,
    vehicleExpertise,
    vehicle,
    supervisor,
    counterInspection,
    reconditionCostInspection,
    reconditionCostCounterInspection,
    countryPosId,
    newSuggestOffer,
    MileageGap,
    CounterAppraisalAnalys,
    tooltipOpenSecond,
    setTooltipOpenSecond,
    open,
    toggleAccordion,
    openDetails,
    toggleDetails,
  } = useCounterAppraisalAnalysis();

  if (!CounterAppraisalAnalys) return <></>;
  return (
    <div className="autobiz-ai-bloc overflow-auto mt-4 accordion">
      <div
        className="accordion-header"
        role="button"
        onClick={() => toggleAccordion()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            toggleAccordion();
          }
        }}
        tabIndex={0}
      >
        <Label className="text-primary font-weight-bold text-left">
          <FontAwesomeIcon style={{ width: 30 }} icon={open ? faChevronDown : faChevronRight} />
          {t('counter_appraisal')}
        </Label>
        {!open && (
          <span style={{ paddingLeft: 30 }}>
            {t('priceGapCa')}:{' '}
            {currencySign(Math.abs(parseFloat(supervisor?.c2bOffer - newSuggestOffer)), null, countryPosId)}
          </span>
        )}
      </div>

      <div className="accordion-body" style={{ display: open ? 'block' : 'none' }}>
        <div>
          <span className="trustScore mr-3">{t('gapAnalysis')}</span>
          <FontAwesomeIcon
            icon={faInfoCircle}
            size="1x"
            id="gapAnalysisInformation"
            className="info-icon position-absolute ml-n2 mt-1"
            onClick={() => setTooltipOpenSecond((prev) => !prev)}
          />
        </div>
        <Tooltip
          placement="bottom"
          isOpen={tooltipOpenSecond}
          target="gapAnalysisInformation"
          toggle={() => !isMobile && setTooltipOpenSecond((prev) => !prev)}
        >
          {t('gapAnalysisInformation')}
        </Tooltip>

        <Table className="mb-0">
          <thead className="offer-table-header">
            <tr>
              <th className="w-30 px-0">
                <></>
              </th>
              <th
                className={`font-weight-normal border-right border-#dee2e6 text-left 
                  ${isMobile ? 'text-primary font-weight-bold' : ''}`}
              >
                {t('appraisal')}
              </th>
              <th
                className={`font-weight-normal border-right border-#dee2e6 text-left 
                  ${isMobile ? 'text-primary font-weight-bold' : ''}`}
              >
                {isMobile ? t('counter_appraisal') : t('counter_appraisal')}
              </th>
              <th className={`font-weight-normal text-left ${isMobile ? 'text-primary font-weight-bold' : ''}`}>
                {t('Gap')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="d-flex">
                  <span className={`numberDamagedPart ${isMobile ? 'font-weight-bold' : ''}`}>
                    {t('entryStockDate')}
                  </span>
                </div>
              </td>
              <td className="border-right border-#dee2e6 text-lightgray">
                {moment(supervisor?.entryStockDate?.date).format('DD/MM/YYYY')}
              </td>
              <td className="border-right border-#dee2e6 text-lightgray">
                {moment(counterInspection?.createdAt?.date).format('DD/MM/YYYY')}
              </td>
              <td className="text-lightgray">
                {moment(counterInspection?.createdAt?.date).diff(moment(supervisor?.entryStockDate?.date), 'days')}
                {t('days')}
              </td>
            </tr>
            <tr>
              <td>
                <span className={`bodyRefurbishmentPricing ${isMobile ? 'font-weight-bold' : ''}`}>{t('mileage')}</span>
              </td>
              <td className="border-right border-#dee2e6 text-lightgray">
                {vehicleExpertise?.mileage?.toLocaleString('en-US')?.replace(/,/g, ' ')}
              </td>
              <td className="border-right border-#dee2e6 text-lightgray">
                {counterInspection?.mileage?.toLocaleString('en-US')?.replace(/,/g, ' ')}
              </td>
              <td className="text-lightgray">
                <NumberDisplay gap={MileageGap} />{' '}
                {`${MileageGap?.toLocaleString('en-US')?.replace(/,/g, ' ')} ${t('km')}`}
              </td>
            </tr>
            <tr>
              <td>
                <span className={`bodyRefurbishmentPricing ${isMobile ? 'font-weight-bold' : ''}`}>{t('version')}</span>
              </td>
              <td className="border-right border-#dee2e6 text-lightgray"> {vehicle?.versionList}</td>
              <td className="border-right border-#dee2e6 text-lightgray">{counterInspection?.versionName}</td>
              <td className="text-lightgray">
                {vehicle?.versionList === counterInspection?.versionName ? t('noChange') : t('changeVersion')}
              </td>
            </tr>
            <tr>
              <td>
                <span className={`bodyRefurbishmentPricing ${isMobile ? 'font-weight-bold' : ''}`}>
                  {!isMobile ? t('reconditionCost') : t('Recon. cost')}
                </span>
              </td>
              <td className="border-right border-#dee2e6 text-lightgray">
                {currencySign(
                  reconditionCostInspection?.toLocaleString('en-US')?.replace(/,/g, ' '),
                  null,
                  countryPosId,
                )}
              </td>
              <td className={` border-right border-#dee2e6 text-lightgray`}>
                {currencySign(
                  reconditionCostCounterInspection?.toLocaleString('en-US')?.replace(/,/g, ' '),
                  null,
                  countryPosId,
                )}
              </td>
              <td className="text-lightgray">
                <NumberDisplay gap={reconditionCostCounterInspection - reconditionCostInspection} />{' '}
                {currencySign(
                  (reconditionCostCounterInspection - reconditionCostInspection)
                    ?.toLocaleString('en-US')
                    ?.replace(/,/g, ' '),
                  null,
                  countryPosId,
                )}
              </td>
            </tr>
            {openDetails && (
              <>
                <tr>
                  <td className="py-0 pl-4">
                    <span className="bodyRefurbishmentPricing">{t('bodywork')}</span>
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(expertiseCosts?.bodyCosts), null, countryPosId)}
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(counterInspection?.bodyCosts), null, countryPosId)}
                  </td>
                  <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    <NumberDisplay gap={gapBodywork} /> {currencySign(_.round(gapBodywork), null, countryPosId)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0 pl-4">
                    <span className="bodyRefurbishmentPricing">{t('wheels')}</span>
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(expertiseCosts?.pneumaticCosts), null, countryPosId)}
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(counterInspection?.pneumaticCosts), countryPosId)}
                  </td>
                  <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    <NumberDisplay gap={gapWheels} /> {currencySign(_.round(gapWheels), null, countryPosId)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0 pl-4">
                    <span className="bodyRefurbishmentPricing">{t('servicing')}</span>
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(expertiseCosts?.servicingCosts), null, countryPosId)}
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(counterInspection?.servicingCosts), null, countryPosId)}
                  </td>
                  <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    <NumberDisplay gap={gapServicing} /> {currencySign(_.round(gapServicing), null, countryPosId)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0 pl-4">
                    <span className="bodyRefurbishmentPricing">{t('Interior')}</span>
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(expertiseCosts?.innerCosts), null, countryPosId)}
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(counterInspection?.innerCosts), null, countryPosId)}
                  </td>
                  <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    <NumberDisplay gap={gapInterior} /> {currencySign(_.round(gapInterior), null, countryPosId)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0 pl-4">
                    <span className="bodyRefurbishmentPricing">{t('road_test')}</span>
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(expertiseCosts?.roadTestCosts), null, countryPosId)}
                  </td>
                  <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    {currencySign(_.round(counterInspection?.roadTestCosts), null, countryPosId)}
                  </td>
                  <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                    <NumberDisplay gap={gapRoadTest} /> {currencySign(_.round(gapRoadTest), null, countryPosId)}
                  </td>
                </tr>
                  <tr>
                    <td className="py-0 pl-4">
                      <span className="bodyRefurbishmentPricing">{t('motor')}</span>
                    </td>
                    <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                      {currencySign(_.round(expertiseCosts?.mechanicalCosts), null, countryPosId)}
                    </td>
                    <td className={`border-right border-#dee2e6 text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                      {currencySign(_.round(counterInspection?.mechanicalCosts), null, countryPosId)}
                    </td>
                    <td className={`text-lightgray py-0 ${isMobile ? '' : 'pl-4'}`}>
                      <NumberDisplay gap={gapMotor} /> {currencySign(_.round(gapMotor), null, countryPosId)}
                    </td>
                  </tr>
              </>
            )}
            <tr>
              <td colSpan="4" style={{ paddingLeft: 0 }}>
                <button type="button" className="btn btn-link pl-0 see-more" onClick={() => toggleDetails()}>
                  <FontAwesomeIcon style={{ width: 30 }} icon={openDetails ? faChevronUp : faChevronDown} />
                  {openDetails ? t('hideDetailedReconCosts') : t('showDetailedReconCosts')}
                </button>
              </td>
            </tr>
          </tbody>
        </Table>
        <Col style={{ marginTop: '2rem' }}>
          <Label className="text-primary  text-center">
            {t('initialOffer')} : {currencySign(parseFloat(supervisor?.c2bOffer).toFixed(0), null, countryPosId)}
          </Label>
          <Label
            className="text-primary font-weight-bold text-center"
            style={{ fontSize: isMobile ? '1rem' : '1.5rem' }}
          >
            {t('newSuggestedOffer')} :{' '}
            {currencySign(newSuggestOffer?.toLocaleString('en-US')?.replace(/,/g, ' '), null, countryPosId)}
          </Label>
        </Col>
        <div className="d-flex justify-content-center align-center  mt-5 analysis-tab-gap">
          <div className="w-30  d-lg-inline">
            <a
              target="_blank"
              href={vehicleExpertise?.pdfUrl}
              className="download-btn custom-padding"
              rel="noopener noreferrer"
              id="theHrefMonkReport"
            >
              <span className="d-inline-block  d-lg-inline-block px-2">{t('seePdfMainAppraisal')}</span>
            </a>
          </div>
          <div className="w-30 d-lg-inline">
            <a
              target="_blank"
              href={counterInspection?.pdfUrl}
              className="download-btn custom-padding"
              rel="noopener noreferrer"
              id="theHrefMonkReport"
            >
              <span className="d-inline-block  d-lg-inline-block px-2">{t('seePdfCounterAppraisal')}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const NumberDisplay = ({ gap }) => {
  if (gap > 0) {
    return <span style={{ color: 'red' }}>+</span>;
  }
  return null;
};
NumberDisplay.propTypes = {
  gap: PropTypes.number.isRequired,
};
export default CounterAppraisalAnalysis;
